<template>
    <div class="food-v3-container">
        <!-- Stats Header Cards (Directly on dark page background, NOT in white card) -->
        <FoodStatsNavigation
            v-if="!['tables', 'table-categories'].includes(activeTab)"
            :stats="stats"
            :activeTab="activeTab"
            @click="setActiveTab"
        />
        <TableStatsNavigation v-else :stats="stats" :activeTab="activeTab" @click="setActiveTab" />

        <!-- Main Content Card (White background - only this part) -->
        <div class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Tab Title (hidden when filter/breadcrumb is active) -->
            <div v-if="!categoryFilter && !hasBreadcrumb" class="ppv2-flex ppv2-items-center ppv2-justify-between">
                <Header :title="tabTitle" />
            </div>

            <!-- Loading State -->
            <div v-if="isLoading" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-20">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-10 ppv2-w-10 ppv2-border-4 ppv2-border-purple-600 ppv2-border-t-transparent"
                ></div>
            </div>

            <!-- Tab Content -->
            <component
                v-else
                :is="currentTabComponent"
                ref="tabComponent"
                :key="activeTab + '-' + (categoryFilter ? categoryFilter.slug : 'all')"
                :categoryFilter="categoryFilter"
                :establishment="establishment"
                v-bind="tabExtraProps"
                @refresh-stats="fetchStats"
                @view-products="handleViewProducts"
                @clear-filter="clearCategoryFilter"
                @view-table-categories="setActiveTab('table-categories')"
                @view-tables="setActiveTab('tables')"
                @breadcrumb-change="handleBreadcrumbChange"
                :token="token"
            />
        </div>
    </div>
</template>

<script>
import { Package } from 'lucide-vue'
import Carte from './Carte.vue'
import Categories from './Categories.vue'
import Packs from './Packs.vue'
import Tables from './Tables.vue'
import TableCategories from './TableCategories.vue'
import Options from './Options.vue'
import Tags from './Tags.vue'
import FoodStatsNavigation from './FoodStatsNavigation.vue'
import TableStatsNavigation from './TableStatsNavigation.vue'
import normalizeSize from '../../../utils/normalizeSize'
import Header from '../../Shared/Header.vue'
export default {
    name: 'FoodV3Index',
    components: {
        Header,
        Package,
        Carte,
        Categories,
        Packs,
        Tables,
        TableCategories,
        Options,
        FoodStatsNavigation,
        TableStatsNavigation,
        Tags,
    },
    props: {
        initialTab: {
            type: String,
            default: 'carte',
        },
        establishment: {
            type: Object,
            default: () => ({}),
        },
        token: {
            type: String,
            default: '',
        },
        /** Permission « Modifier un produit » transmise par Blade (`can-edit-stock`) ; le serveur fait foi. */
        canEditStock: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeTab: this.initialTab,
            isLoading: true,
            categoryFilter: null, // { id, slug, name } - for filtering products by category
            hasBreadcrumb: false, // true when Categories has active breadcrumbs
            stats: {
                products: 0,
                categories: 0,
                packs: 0,
                options: 0,
                tables: 0,
                table_categories: 0,
                tags: 0,
                stock_movements_today: 0,
            },
            // Expose icons to template
        }
    },
    computed: {
        tabTitle() {
            const titles = {
                carte: this.$t('food.navigation.menu'),
                categories: this.$t('food.navigation.categories'),
                //packs: this.$t('food.navigation.packs'),
                options: this.$t('food.navigation.options'),
                tables: this.$t('food.navigation.qr_codes'),
                'table-categories': this.$t('food.navigation.table_categories'),
                tags: this.$t('food.navigation.tags'),
            }
            return titles[this.activeTab] || this.$t('food.navigation.menu')
        },
        currentTabComponent() {
            const components = {
                carte: 'Carte',
                categories: 'Categories',
                // packs: 'Packs',
                options: 'Options',
                tables: 'Tables',
                'table-categories': 'TableCategories',
                tags: 'Tags',
            }
            return components[this.activeTab] || 'Produits'
        },
        /** Props propres à un onglet (évite de poser `can-edit-stock` en attribut DOM sur les autres). */
        tabExtraProps() {
            return this.activeTab === 'carte' ? { canEditStock: this.canEditStock } : {}
        },
    },
    methods: {
        normalizeSize,
        /**
         * Change d'onglet en consultant d'abord la garde de sortie de l'onglet courant
         * (`confirmLeave`, exposée par le mixin d'édition de stock de Carte). `force` court-circuite.
         */
        setActiveTab(tab, force = false) {
            const current = this.$refs.tabComponent
            if (!force && tab !== this.activeTab && current && typeof current.confirmLeave === 'function') {
                current.confirmLeave(() => this.applyActiveTab(tab))
                return
            }
            this.applyActiveTab(tab)
        },
        applyActiveTab(tab) {
            this.activeTab = tab
            // Update URL query parameter without page reload
            const url = new URL(window.location.href)
            url.searchParams.set('tab', tab)
            window.history.replaceState({}, '', url)
            // Clear category filter when manually switching tabs
            if (tab !== 'carte') {
                this.categoryFilter = null
            }
            // Reset breadcrumb state when switching tabs
            this.hasBreadcrumb = false
        },
        handleViewProducts(category) {
            // Switch to products tab with category filter
            this.categoryFilter = {
                id: category.categoryId,
                slug: category.categorySlug,
                name: category.categoryName,
            }
            this.activeTab = 'carte'
        },
        clearCategoryFilter() {
            this.categoryFilter = null
        },
        handleBreadcrumbChange(hasBreadcrumb) {
            this.hasBreadcrumb = hasBreadcrumb
        },
        async fetchStats() {
            try {
                const response = await fetch('/api/v3/food/stats')
                const json = await response.json()
                if (json.success) {
                    this.stats = json.data
                }
                await this.fetchTableStats()
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isLoading = false
            }
        },
        async fetchTableStats() {
            try {
                const response = await fetch('/back-office/v3/food/table-categories/stats')
                const json = await response.json()
                if (json.success) {
                    this.stats = { ...this.stats, ...json.data }
                }
            } catch (error) {
                this.$toast?.error('Impossible de charger les statistiques des tables')
            }
        },
    },
    mounted() {
        this.fetchStats()
        // Remove white background from parent container for Food V3 layout
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('food-v3-no-bg')
        }
    },
    beforeDestroy() {
        // Restore background when leaving the page
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.remove('food-v3-no-bg')
        }
    },
}
</script>

<style scoped lang="scss">
.food-v3-container {
    padding: 0;
}

// `.statsCard` / `.statsCardActive` vivent désormais dans StatsCard.vue (partagés avec la page Mouvements).
</style>

<!-- Non-scoped style to override parent container background -->
<style lang="scss">
// Override the main-container white background for Food V3 module
// Using class added via JavaScript for better browser compatibility
#main-container.food-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}

// Also support the :has() selector for modern browsers
#main-container:has(.food-v3-container) {
    background: transparent !important;
}

// Dark mode support
.dark #main-container.food-v3-no-bg,
.dark #main-container:has(.food-v3-container) {
    background: transparent !important;
}
</style>
